import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker, Form, Select } from 'antd';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { PrivateDocumentCategoryToText } from '@/entities/document';
import { AssetItem } from '@/entities/interwills';
import { useIwEditingStore } from '@/entities/interwills';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { CollectionName } from '@/shared/lib/sj-orm';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import {
  PrivateDocumentCategory,
  PrivateDocumentDto,
} from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ProofOfOwnershipModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type ProofOfOwnershipParams = {
  documentType: PrivateDocumentCategory;
  documentName: string;
  country: string;
  documentNumber: string;
  issueDate: Dayjs;
};

export const ProofOfOwnershipModal: FC<ProofOfOwnershipModalProps> = ({ assetId }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'proofOfOwnership' });
  const { t: td } = useTranslation('documents');

  const { updateAssets, assets } = useIwEditingStore();

  const modals = useModalsStore();
  const { keyPair } = useKeyPairStore();
  const sjDatabase = useSJDatabase();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<ProofOfOwnershipParams>();

  const onFinishHandler = async (values: ProofOfOwnershipParams) => {
    const newDoc: PrivateDocumentDto & { startDate: string; number: string } = {
      id: sjDatabase.generateId(CollectionName.PRIVATE_DOCUMENTS),
      name: values.documentName,
      privateCategory: values.documentType,
      type: DocumentType.PRIVATE_DOCUMENTS,
      country: values.country,
      startDate: dates.dayjsToIsoDateTime(values.issueDate),
      number: values.documentNumber,
    };

    const document = await sjDatabase.collections.privateDocuments?.create(newDoc, [
      keyPair?.publicKey || '',
    ]);

    const newAssets = [...assets];
    const idx = newAssets.findIndex((item) => item.id === assetId);
    if (document) {
      const updatedAsset: AssetItem = {
        ...newAssets[idx],
        otherDocuments: [...(newAssets[idx].otherDocuments ?? []), document.id],
      };

      await sjDatabase.collections.assets?.update(updatedAsset);

      newAssets[idx].proofOfOwnership = document;
      updateAssets(newAssets);
    }
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm
        style={{
          gap: 24,
        }}
        onFinish={formHandleSubmit(onFinishHandler)}
      >
        <Controller
          name={'documentType'}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <FieldContainerIw label={t(`${field.name}.label`)} required>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Select
                    options={Object.entries(PrivateDocumentCategoryToText).map(([key, value]) => ({
                      label: td(value),
                      value: key,
                    }))}
                    placeholder={t(`${field.name}.placeholder`)}
                    {...field}
                  />
                </Form.Item>
              </FieldContainerIw>
            );
          }}
        />
        <Controller
          name={'documentName'}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <FieldContainerIw label={t(`${field.name}.label`)} required>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input placeholder={t(`${field.name}.placeholder`)} type={'text'} {...field} />
                </Form.Item>
              </FieldContainerIw>
            );
          }}
        />
        <Controller
          name={'country'}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <FieldContainerIw label={t(`${field.name}.label`)} required>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <SelectCountry {...field} />
                </Form.Item>
              </FieldContainerIw>
            );
          }}
        />
        <Row>
          <Controller
            name={'documentNumber'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input placeholder={t(`${field.name}.placeholder`)} type={'text'} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'issueDate'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                        height: 40,
                      }}
                      format={'DD MMMM YYYY'}
                      placeholder={t(`${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </Row>
        <Buttons>
          <ButtonIw htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 32px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
`;
